<template>
  <div class="flex-1 w-[63.2rem]">
    <launch-setting-content
      v-if="!isShowTabList"
      :key="forceRenderKey"
      :installationPathAndPcClientSettings="installationPathAndPcClientSettings"
      :runOptions="runOptions"
      :gameId="gameId"
      :typeGame="typeGame"
      :releaseType="releaseType"
      :planStatus="planStatus"
      :verifyStatus="verifyStatus"
      :isCallClearRunOptionsApi="isCallClearRunOptionsApi"
      @onShowExecutionNameForSamePlatform="(value: boolean) => isShowPcExecutionName = value"
      @onUpdatePlatformGuide="(platforms: string[]) => onUpdatePlatformGuide(platforms)"
      @resetClearRunOptionsApi="setIsCallClearRunOptionsApi(false)"
    />

    <!-- When released, show tabs with both components -->
    <s-tabs
      v-else
      v-model="selectedTab"
      defaultValue="'tab02'"
      variant="line-fixed"
      size="lg"
      :defaultIndex="0"
      class="relative stove-studio-tab-line larger"
    >
      <product-guide class="!max-w-[39rem] w-[39rem] mb-[-.4rem]" topStart :storedName="`${IS_CLOSE_LAUNCH_SETTING_GUIDE}_${productNo}`">
        <s-text as="h4" role="text4" class="font-bold">
          <safe-html :html="$t('studio.execution_setting.test_package_tab_tooltip_title')" />
        </s-text>
        <s-text as="p" role="cap1">
          <safe-html :html="$t('studio.execution_setting.test_package_tab_tooltip_msg')" />
        </s-text>
      </product-guide>
      <s-tab-list class="bg-transparent w-max max-w-full">
        <s-tab-item :value="RUN_OPTION_STATUS.TEST" class="min-w-136 min-h-48 !basis-[unset]">
          <span class="px-12 py-[.9rem] text-xl leading-xl text-center flex items-center justify-center gap-4">
            {{ $t('studio.execution_setting.test_tab') }}
          </span>
        </s-tab-item>
        <s-tab-item v-if="comingSoonRunOptions.length > 0" :value="RUN_OPTION_STATUS.COMING_SOON" class="min-w-136 min-h-48 !basis-[unset]">
          <span class="px-12 py-[.9rem] text-xl leading-xl text-center flex items-center justify-center gap-4">
            {{ liveRunOptions.length > 0 ? $t('studio.execution_setting.scheduled_tab') : $t('studio.execution_setting.live_tab') }}
          </span>
        </s-tab-item>
        <s-tab-item v-if="liveRunOptions.length > 0" :value="RUN_OPTION_STATUS.LIVE" class="min-w-136 min-h-48 !basis-[unset]">
          <span class="px-12 py-[.9rem] text-xl leading-xl text-center flex items-center justify-center gap-4">
            {{ $t('studio.execution_setting.live_tab') }}
          </span>
        </s-tab-item>
      </s-tab-list>
      <s-tab-panels>
        <s-tab-panel :value="RUN_OPTION_STATUS.TEST" :class="{'mt-24': isWebPlatform}">
          <st-box v-if="!isWebPlatform" class="!rounded-[2.8rem] !py-24 mt-24 mb-24">
            <s-text as="h3" role="title8" class="text-on-surface-elevation-2">
              <safe-html :html="$t('studio.execution_setting.test_package_guide1')" />
            </s-text>
            <s-text as="p" role="cap1" class="text-on-surface-elevation-3 mt-4">
              <safe-html :html="$t('studio.execution_setting.test_package_guide2')" />
            </s-text>
            <s-button
              variant="outline"
              icon="ic-v2-control-add-line"
              size="sm"
              class="w-fit mt-16"
              @click="onLoadLiveSettings"
            >
              {{ $t('studio.execution_setting.fetch_live_package_data_btn') }}
            </s-button>
          </st-box>

          <launch-setting-content
            :key="`test-tab-${forceRenderKey}`"
            :installationPathAndPcClientSettings="testTabSettings"
            :runOptions="testRunOptions"
            :originalRunOptions="originalRunOptions"
            :originalTabSettings="originalTabSettings"
            :gameId="gameId"
            :typeGame="typeGame"
            :releaseType="releaseType"
            :planStatus="planStatus"
            :verifyStatus="verifyStatus"
            :tabName="RUN_OPTION_STATUS.TEST"
            :disabledDefaultPlatform="true"
            :isCallClearRunOptionsApi="isCallClearRunOptionsApi"
            @onShowExecutionNameForSamePlatform="(value: boolean) => isShowPcExecutionName = value"
            @onUpdatePlatformGuide="(platforms: string[]) => onUpdatePlatformGuide(platforms)"
            @resetClearRunOptionsApi="setIsCallClearRunOptionsApi(false)"
          />
        </s-tab-panel>
        <s-tab-panel v-if="comingSoonRunOptions.length > 0" :value="RUN_OPTION_STATUS.COMING_SOON" class="mt-24">
          <div v-if="!isWebPlatform" class="flex flex-col text-center gap-4 items-center !rounded-[2.8rem] !py-20 !px-[1rem] mt-24 bg-brand-primary">
            <s-text as="h3" role="title6" class="flex items-center gap-4 text-white !font-bold">
              <s-icon icon="ic-v2-state-info-circle-line" size="4xl" class="mt-[-.2rem]" />
              {{ getDateTimeByLocale(comingSoonDetails?.buildSchedule.scheduledStartedAt!, true) }}
            </s-text>
            <s-text as="p" role="text1" class="text-white font-medium">{{ $t('studio.execution_setting.scheduled_info_guide') }}</s-text>
          </div>
          <st-box v-if="!isWebPlatform" class="flex flex-col gap-24 !py-[3rem] my-24">
            <s-text as="h3" role="text1" class="text-on-surface-elevation-2 font-bold">
              {{ $t('studio.execution_setting.build_info') }}
            </s-text>
            <div class="flex flex-col gap-4">
              <s-text as="p" role="text3" class="text-on-surface-elevation-2 font-bold">
                {{ comingSoonDetails?.buildNo }}
              </s-text>
              <s-text as="p" role="btn2" class="text-on-surface-elevation-3">
                {{ comingSoonDetails?.buildDescription }}
              </s-text>
            </div>
          </st-box>
          <launch-setting-content
            :key="`coming-soon-tab-${forceRenderKey}`"
            :installationPathAndPcClientSettings="comingSoonTabSettings"
            :runOptions="comingSoonRunOptions"
            :gameId="gameId"
            :typeGame="typeGame"
            :releaseType="releaseType"
            :planStatus="planStatus"
            :verifyStatus="verifyStatus"
            :tabName="RUN_OPTION_STATUS.COMING_SOON"
            :viewOnly="true"
            :isCallClearRunOptionsApi="isCallClearRunOptionsApi"
            @onShowExecutionNameForSamePlatform="(value: boolean) => isShowPcExecutionName = value"
            @onUpdatePlatformGuide="(platforms: string[]) => onUpdatePlatformGuide(platforms)"
            @resetClearRunOptionsApi="setIsCallClearRunOptionsApi(false)"
          />
          <div class="flex justify-center">
            <s-button
              variant="primary"
              size="lg"
              class="w-fit mt-16"
              @click="onCancelComingSoon"
            >
              {{ $t('studio.execution_setting.scheduled_pack_cancel_btn') }}
            </s-button>
          </div>
        </s-tab-panel>
        <s-tab-panel :value="RUN_OPTION_STATUS.LIVE" class="mt-24">
          <st-box v-if="!isWebPlatform" class="flex flex-col gap-24 !py-[3rem] my-24">
            <s-text as="h3" role="text1" class="text-on-surface-elevation-2 font-bold">
              {{ $t('studio.execution_setting.build_info') }}
            </s-text>
            <div class="flex flex-col gap-4">
              <s-text as="p" role="text3" class="text-on-surface-elevation-2 font-bold">
                {{ liveDetails?.buildNo }}
              </s-text>
              <s-text as="p" role="btn2" class="text-on-surface-elevation-3">
                {{ liveDetails?.buildDescription }}
              </s-text>
              <s-text v-if="liveDetails?.releaseStatus === RELEASE_STATUS_BUILD.COMING_SOON && dateSettings" as="p" role="text3" class="text-on-surface-elevation-3">
                {{ dateSettings }}
                <s-text as="span" role="text3" class="text-brand-primary">{{ $t('studio.execution_setting.live_apply_popup_title') }}</s-text>
              </s-text>
            </div>
          </st-box>
          <launch-setting-content
            :key="`live-tab-${forceRenderKey}`"
            :installationPathAndPcClientSettings="liveTabSettings"
            :runOptions="liveRunOptions"
            :gameId="gameId"
            :typeGame="typeGame"
            :releaseType="releaseType"
            :planStatus="planStatus"
            :verifyStatus="verifyStatus"
            :tabName="RUN_OPTION_STATUS.LIVE"
            :viewOnly="true"
            :isCallClearRunOptionsApi="isCallClearRunOptionsApi"
            @onShowExecutionNameForSamePlatform="(value: boolean) => isShowPcExecutionName = value"
            @onUpdatePlatformGuide="(platforms: string[]) => onUpdatePlatformGuide(platforms)"
            @resetClearRunOptionsApi="setIsCallClearRunOptionsApi(false)"
          />
        </s-tab-panel>
      </s-tab-panels>
    </s-tabs>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { deleteBuildScheduleApi, fetchBuildsPlanDetailApi, fetchInstallPathAndPcClientSettingsOnTabApi } from '@/apis/launch-settings.api';
import safeHtml from '@/components/common/safe-html.vue';
import StBox from '@/components/common/st-box.vue';
import LaunchSettingContent from '@/components/launch-setting/tabs/launch-setting-content.vue';
import ProductGuide from '@/components/product/product-guide.vue';
import { showConfirm } from '@/composables/useDialog';
import { RELEASE_STATUS_BUILD } from '@/constants/build.const';
import { PLATFORM } from '@/constants/common.const';
import { RUN_OPTION_STATUS, type RunOptionStatusType } from '@/constants/launch-setting.const';
import type { RunOptionGroup } from '@/types/launch-settings/launch-settings.type';
import type { BuildsPlanDetailResponse, InstallPathAndPcClientSettingsResponse } from '@/types/launch-settings/launch-settings-response.type';
import { getDateTimeByLocale } from '@/utils/date.util';

const IS_CLOSE_LAUNCH_SETTING_GUIDE = 'isClosedLaunchSettingGuide';

const route = useRoute();

const productNo = route.params.productId as string;

const { t } = useI18n();
const router = useRouter();

interface LaunchGameDemoUltilityProps {
  typeGame: string;
  gameId: string;
  releaseType: string;
  verifyStatus: string;
  planStatus: string;
  installationPathAndPcClientSettings: InstallPathAndPcClientSettingsResponse | undefined;
  runOptions: Array<RunOptionGroup>;
}

const props = defineProps<LaunchGameDemoUltilityProps>();
const comingSoonDetails = ref<BuildsPlanDetailResponse>();
const liveDetails = ref<BuildsPlanDetailResponse>();

const emit = defineEmits<{
  onShowExecutionNameForSamePlatform: [v: boolean];
  onUpdatePlatformGuide: [v: Array<string>];
  'update:runOptions': [v: Array<RunOptionGroup>];
  'update:installationPathAndPcClientSettings': [v: InstallPathAndPcClientSettingsResponse | undefined];
}>();

const dateSettings = computed(
  () => {
    const date = liveDetails.value?.buildSchedule.startedAt ?? liveDetails.value?.buildSchedule.scheduledStartedAt;
    if (date) {
      return getDateTimeByLocale(date, true);
    }
    return '';
  }
);

const isCallClearRunOptionsApi = ref<boolean>(false);

// Release check
const isShowTabList = computed(
  () => comingSoonRunOptions.value.length > 0 || liveRunOptions.value.length > 0
);

// Local state
const isShowPcExecutionName = ref<boolean>(false);

// Tab management
const selectedTab = ref<RunOptionStatusType>(RUN_OPTION_STATUS.TEST);
// Add forceRenderKey to force re-render component when data changes
const forceRenderKey = ref<number>(0);

// Store tab-specific installation settings
const testTabSettings = ref<InstallPathAndPcClientSettingsResponse | undefined>(undefined);
const comingSoonTabSettings = ref<InstallPathAndPcClientSettingsResponse | undefined>(undefined);
const liveTabSettings = ref<InstallPathAndPcClientSettingsResponse | undefined>(undefined);

// Set initial tab based on available options
const initializeSelectedTab = () => {
  // Check URL query parameter first
  const tabParam = router.currentRoute.value.query.tab as string;

  if (tabParam === RUN_OPTION_STATUS.LIVE && liveRunOptions.value.length > 0) {
    selectedTab.value = RUN_OPTION_STATUS.LIVE;
    return;
  }

  if (tabParam === RUN_OPTION_STATUS.COMING_SOON && comingSoonRunOptions.value.length > 0) {
    selectedTab.value = RUN_OPTION_STATUS.COMING_SOON;
    return;
  }

  if (liveRunOptions.value.length > 0) {
    selectedTab.value = RUN_OPTION_STATUS.LIVE;
  } else if (comingSoonRunOptions.value.length > 0) {
    selectedTab.value = RUN_OPTION_STATUS.COMING_SOON;
  } else if (testRunOptions.value.length > 0) {
    selectedTab.value = RUN_OPTION_STATUS.TEST;
  }
};

// Computed properties to filter run options by status
const testRunOptions = computed<Array<RunOptionGroup>>(() =>
  props.runOptions.filter((option: RunOptionGroup) => option.status === RUN_OPTION_STATUS.TEST)
);

const comingSoonRunOptions = computed<Array<RunOptionGroup>>(() =>
  props.runOptions.filter((option: RunOptionGroup) => option.status === RUN_OPTION_STATUS.COMING_SOON)
);

// Computed property to filter runOptions by status is LIVE
const liveRunOptions = computed<Array<RunOptionGroup>>(() =>
  props.runOptions.filter((option: RunOptionGroup) => option.status === RUN_OPTION_STATUS.LIVE)
);

// Computed property to get runOption for live | coming soon
const originalRunOptions = computed<Array<RunOptionGroup>>(() => {
  if (liveRunOptions.value && liveRunOptions.value.length > 0) {
    // Store original live data for comparison
    return liveRunOptions.value;
  } else if (comingSoonRunOptions.value && comingSoonRunOptions.value.length > 0) {
    return comingSoonRunOptions.value;
  }

  return [];
});

const originalTabSettings = computed<InstallPathAndPcClientSettingsResponse | undefined>(() => {
  if (liveRunOptions.value && liveRunOptions.value.length > 0) {
    return liveTabSettings.value;
  } else if (comingSoonRunOptions.value && comingSoonRunOptions.value.length > 0) {
    return comingSoonTabSettings.value;
  }

  return undefined;
});

const isWebPlatform = computed(() => {
  return originalRunOptions.value?.[0]?.type === PLATFORM.WEB;
});

// Function to handle and forward the platform guide update
const onUpdatePlatformGuide = (platforms: Array<string>) => {
  emit('onUpdatePlatformGuide', platforms);
};

watch(comingSoonRunOptions, async () => {
  if (comingSoonRunOptions.value.length > 0 && comingSoonRunOptions.value[0].buildId) {
    comingSoonDetails.value = await fetchBuildsPlanDetailApi({
      productNo: Number(productNo),
      buildId: comingSoonRunOptions.value[0].buildId
    });
  }
}, { immediate: true });

watch(liveRunOptions, async () => {
  if (liveRunOptions.value.length > 0 && liveRunOptions.value[0].buildId) {
    liveDetails.value = await fetchBuildsPlanDetailApi({
      productNo: Number(productNo),
      buildId: liveRunOptions.value[0].buildId
    });
  }
}, {
  immediate: true
});

// Watch for tab changes to fetch installation settings for that specific tab
watch(selectedTab, async (newTab: RunOptionStatusType) => {
  if (!props.gameId) { return; }

  try {
    // Only fetch if we don't already have settings for this tab
    let shouldFetch = false;

    if (newTab === RUN_OPTION_STATUS.TEST && !testTabSettings.value) {
      shouldFetch = true;
    } else if (newTab === RUN_OPTION_STATUS.COMING_SOON && !comingSoonTabSettings.value) {
      shouldFetch = true;
    } else if (newTab === RUN_OPTION_STATUS.LIVE && !liveTabSettings.value) {
      shouldFetch = true;
    }

    if (shouldFetch) {
      const tabSettings = await fetchInstallPathAndPcClientSettingsOnTabApi(props.gameId, newTab);

      if (tabSettings) {
        if (newTab === RUN_OPTION_STATUS.TEST) {
          testTabSettings.value = tabSettings;
        } else if (newTab === RUN_OPTION_STATUS.COMING_SOON) {
          comingSoonTabSettings.value = tabSettings;
        } else if (newTab === RUN_OPTION_STATUS.LIVE) {
          liveTabSettings.value = tabSettings;
        }

        // Force re-render of the component
        forceRenderKey.value = Date.now();
      }
    }
  } catch (e) {}
}, { immediate: true });

const copyRunOptionsToTest = (sourceOptions: RunOptionGroup[]): RunOptionGroup[] => {
  if (!sourceOptions || sourceOptions.length === 0) {
    return [];
  }

  // Get current TEST tab options
  // const currentTestOptions = testRunOptions.value;

  // Find highest orderNo in current TEST options
  // let maxOrderNo = -1;
  // if (currentTestOptions.length > 0) {
  //   currentTestOptions.forEach((option: RunOptionGroup) => {
  //     if (option.orderNo > maxOrderNo) {
  //       maxOrderNo = option.orderNo;
  //     }
  //   });
  // }

  // Check if TEST tab has data
  // const existingTestOptionsCount = currentTestOptions.length;

  // Copy all run options and update orderNo, status
  const sourceCopies: RunOptionGroup[] = [];
  sourceOptions.forEach((sourceItem: RunOptionGroup, index: number) => {
    const copy = JSON.parse(JSON.stringify(sourceItem));
    // copy.orderNo = maxOrderNo + 1 + index; // Set next orderNo in sequence
    copy.orderNo = index;
    copy.status = RUN_OPTION_STATUS.TEST;
    copy.id = ''; // Reset ID to avoid conflict
    copy.buildId = null;

    // If TEST tab has data, set isDefault = false and buildId = null
    // if (existingTestOptionsCount > 0) {
    //   copy.isDefault = false;
    //   copy.buildId = null;
    // }

    sourceCopies.push(copy);
  });

  // Keep run options with status other than TEST
  const nonTestOptions = props.runOptions.filter(
    (option: RunOptionGroup) => option.status !== RUN_OPTION_STATUS.TEST
  );

  // Combine: non-TEST options + source copies
  return [...nonTestOptions, ...sourceCopies];
};

const copySettingsToTest = (sourceTabSettings: InstallPathAndPcClientSettingsResponse | undefined) => {
  if (sourceTabSettings) {
    // Copy data from source to TEST, reset ID
    const testSettings = JSON.parse(JSON.stringify(sourceTabSettings));
    testSettings.id = ''; // Reset ID for TEST settings to create new
    testSettings.status = RUN_OPTION_STATUS.TEST; // Set status to TEST
    testSettings.buildId = null; // Reset buildId for TEST settings

    // Update testTabSettings
    testTabSettings.value = testSettings;

    // Emit event to update installationPathAndPcClientSettings in parent
    emit('update:installationPathAndPcClientSettings', testSettings);

    return true;
  }

  return false;
};

const onLoadLiveSettings = async () => {
  isCallClearRunOptionsApi.value = true;

  const result = await showConfirm({
    severity: 'info',
    content: t('studio.execution_setting.fetch_live_data_confirm_popup'),
    confirmLabel: t('studio.execution_setting.fetch_live_data_cf_btn'),
    cancelLabel: t('studio.common.popup_case_cancel_btn'),
    cancelVariant: 'outline',
    panelClasses: 'w-[39.2rem]'
  });

  if (result) {
    // Copy run options from LIVE / COMING_SOON to TEST
    emit('update:runOptions', copyRunOptionsToTest(originalRunOptions.value as Array<RunOptionGroup>));

    // Copy settings from LIVE / COMING_SOON to TEST
    copySettingsToTest(originalTabSettings.value);

    // Switch to TEST tab and force re-render
    selectedTab.value = RUN_OPTION_STATUS.TEST;
    forceRenderKey.value = Date.now();
  }
};

const onCancelComingSoon = async () => {
  const result = await showConfirm({
    severity: 'info',
    content: t('studio.execution_setting.scheduled_pack_cancel_confirm_popup'),
    confirmLabel: t('studio.common.popup_case_cf_btn'),
    cancelLabel: t('studio.common.popup_case_close_btn'),
    cancelVariant: 'outline',
    panelClasses: 'w-[39.2rem]'
  });

  if (result) {
    const success = await deleteBuildScheduleApi(props.gameId, String(comingSoonRunOptions.value[0].buildId));
    if (success) {
      window.location.reload();
    }
  }
};

// Function to initialize tab settings
const initTabSettings = async () => {
  if (!props.gameId) { return; }

  try {
    // Initialize settings for each tab
    if (liveRunOptions.value.length > 0) {
      liveTabSettings.value = await fetchInstallPathAndPcClientSettingsOnTabApi(props.gameId, RUN_OPTION_STATUS.LIVE);
    }

    if (comingSoonRunOptions.value.length > 0) {
      comingSoonTabSettings.value = await fetchInstallPathAndPcClientSettingsOnTabApi(props.gameId, RUN_OPTION_STATUS.COMING_SOON);
    }

    // Always initialize TEST tab settings
    testTabSettings.value = await fetchInstallPathAndPcClientSettingsOnTabApi(props.gameId, RUN_OPTION_STATUS.TEST);

    // If no TEST settings are found, fall back to global settings
    // if (!testTabSettings.value) {
    //   testTabSettings.value = props.installationPathAndPcClientSettings;
    // }

    // Force re-render
    forceRenderKey.value = Date.now();
  } catch (e) {}
};

const init = () => {
  initializeSelectedTab();
  initTabSettings();
};

init();

// Make isCallClearRunOptionsApi accessible outside via refs
const setIsCallClearRunOptionsApi = (value: boolean) => {
  isCallClearRunOptionsApi.value = value;
};

// Add this method to the component's externally accessible methods
defineExpose({
  setIsCallClearRunOptionsApi
});
</script>
